/**
 * «Stampa scadenze» — i pallet scaduti e in scadenza, su carta.
 *
 * Domanda di Enzo (12-10-2026), arrivata a Marina: *quanti pallet a magazzino
 * sono scaduti e quanti stanno per scadere?* Enrico l'ha resa uno strumento
 * completo: un foglio che serva a Enzo per il quadro e ai magazzinieri per
 * andare a prendere i pallet, con fasce di giorni modificabili e di default
 * 30/60/90/120.
 *
 * La finestra mostra **prima** di stampare quanti pallet cadono in ogni fascia,
 * dalla stessa fotografia del PDF (`/magazzino/scadenze` e `/scadenze/pdf`
 * chiamano lo stesso calcolo): chi cambia le fasce vede subito cosa uscirà.
 */
import { Download, Plus, Printer, RotateCcw, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SOGLIE_SCADENZA_DEFAULT, useScadenzeMagazzino } from "@/hooks/useMagazzino";
import { apiDownloadFile } from "@/lib/api";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";

const MAX_FASCE = 6;
const GIORNI_MAX = 730;

/** Gli stessi colori del PDF, dal più urgente: rosso, poi si raffredda. */
const TINTE = ["#b42318", "#c2410c", "#b45309", "#a16207", "#4d7c0f", "#0f766e", "#475569"];

function quando(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PulsanteStampaScadenze() {
  const [aperta, setAperta] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setAperta(true)}>
        <Printer className="size-3.5" />
        Stampa scadenze
      </Button>
      <StampaScadenzeDialog aperta={aperta} onClose={() => setAperta(false)} />
    </>
  );
}

function StampaScadenzeDialog({ aperta, onClose }: { aperta: boolean; onClose: () => void }) {
  const [soglie, setSoglie] = useState<number[]>(SOGLIE_SCADENZA_DEFAULT);
  const [nuova, setNuova] = useState("");
  const [scarico, setScarico] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const { data, isLoading, isFetching } = useScadenzeMagazzino(soglie, aperta);

  const nuovaN = Number(nuova);
  const nuovaValida =
    Number.isInteger(nuovaN) && nuovaN >= 1 && nuovaN <= GIORNI_MAX && !soglie.includes(nuovaN);
  const sonoDefault = soglie.join(",") === SOGLIE_SCADENZA_DEFAULT.join(",");

  function aggiungi() {
    if (!nuovaValida || soglie.length >= MAX_FASCE) return;
    setSoglie([...soglie, nuovaN].sort((a, b) => a - b));
    setNuova("");
  }

  async function scarica() {
    setErrore(null);
    setScarico(true);
    try {
      const oggi = data?.oggi ?? new Date().toISOString().slice(0, 10);
      await apiDownloadFile(
        `/api/v1/magazzino/scadenze/pdf?fasce=${encodeURIComponent(soglie.join(","))}`,
        `pallet-in-scadenza-${oggi}.pdf`,
      );
    } catch (e) {
      setErrore(e instanceof Error ? e.message : "Non sono riuscito a generare il PDF.");
    } finally {
      setScarico(false);
    }
  }

  const ultima = soglie[soglie.length - 1];
  const inScadenza = data?.fasce.slice(1).reduce((s, f) => s + f.pallet, 0) ?? 0;

  return (
    <Dialog open={aperta} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="size-4 text-muted-foreground" />
            Pallet scaduti e in scadenza
          </DialogTitle>
          <DialogDescription>
            Il foglio da stampare: in prima pagina il quadro per fascia e il riepilogo per
            articolo, poi l&apos;elenco dei pallet con lotto, UDM, dove stanno e a quale
            commessa sono destinati.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Fasce, in giorni alla scadenza
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {soglie.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1 rounded-full border bg-card py-0.5 pl-2.5 pr-1 text-xs"
              >
                {s} gg
                <button
                  type="button"
                  aria-label={`Togli la fascia ${s} giorni`}
                  disabled={soglie.length <= 1}
                  onClick={() => setSoglie(soglie.filter((x) => x !== s))}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            {soglie.length < MAX_FASCE && (
              <form
                className="inline-flex items-center gap-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  aggiungi();
                }}
              >
                <input
                  type="number"
                  min={1}
                  max={GIORNI_MAX}
                  value={nuova}
                  onChange={(e) => setNuova(e.target.value)}
                  placeholder="giorni"
                  aria-label="Nuova fascia in giorni"
                  className="h-7 w-20 rounded-md border bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <Button
                  type="submit"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  disabled={!nuovaValida}
                >
                  <Plus className="size-3" />
                  Aggiungi
                </Button>
              </form>
            )}
            {!sonoDefault && (
              <button
                type="button"
                onClick={() => setSoglie(SOGLIE_SCADENZA_DEFAULT)}
                className="ml-1 inline-flex items-center gap-1 text-[11px] text-brand-700 underline-offset-2 hover:underline dark:text-brand-400"
              >
                <RotateCcw className="size-3" />
                30 / 60 / 90 / 120
              </button>
            )}
          </div>
        </div>

        <div
          className={cn(
            "overflow-hidden rounded-lg border",
            isFetching && !isLoading && "opacity-60",
          )}
        >
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">Fascia</th>
                <th className="px-3 py-1.5 text-right font-medium">Pallet</th>
                <th className="px-3 py-1.5 text-right font-medium">Pezzi</th>
                <th className="px-3 py-1.5 text-right font-medium">Articoli</th>
                <th className="px-3 py-1.5 text-right font-medium">A commesse</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || !data
                ? Array.from({ length: soglie.length + 1 }).map((_, i) => (
                    <tr key={i} className="border-t">
                      <td colSpan={5} className="px-3 py-2">
                        <div className="h-4 w-full animate-pulse rounded bg-muted" />
                      </td>
                    </tr>
                  ))
                : data.fasce.map((f, i) => (
                    <tr key={f.chiave} className="border-t" data-fascia={f.chiave}>
                      <td className="px-3 py-1.5">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="size-2 rounded-full"
                            style={{ background: TINTE[Math.min(i, TINTE.length - 1)] }}
                          />
                          {f.etichetta}
                        </span>
                      </td>
                      <td
                        className="px-3 py-1.5 text-right font-semibold tabular-nums"
                        style={f.pallet ? { color: TINTE[Math.min(i, TINTE.length - 1)] } : undefined}
                      >
                        {num(f.pallet)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(Number(f.pezzi))}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(f.articoli)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                        {f.chiave === "scaduti" ? "—" : num(f.destinati)}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        {data && (
          <p className="text-[11.5px] text-muted-foreground">
            Su <b className="text-foreground">{num(data.pallet_a_magazzino)}</b> pallet a
            magazzino: <b className="text-foreground">{num(data.fasce[0]?.pallet ?? 0)}</b> scaduti
            e <b className="text-foreground">{num(inScadenza)}</b> entro {ultima} giorni. Giacenze
            del {quando(data.caricato_il)}.
            {data.senza_scadenza > 0 &&
              ` ${data.senza_scadenza} pallet senza data di scadenza restano fuori dal foglio.`}{" "}
            I pallet scaduti non si assegnano a nessuna commessa.
          </p>
        )}

        {errore && <p className="text-xs text-red-700 dark:text-red-400">{errore}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
          <Button onClick={scarica} disabled={scarico || !data} className="gap-1.5">
            <Download className="size-4" />
            {scarico ? "Preparo il PDF…" : "Scarica PDF"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
